import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { CaptureContext } from '@sentry/types';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';

export function isEnabled() {
  return !!(!window.Cypress && process.env.REACT_APP_SENTRY_DSN);
}

// Returns null if error shouldn't be sent to Sentry
function blockCommonErrorsFromBeingSent(
  event: Sentry.Event
): Sentry.Event | null {
  const expectedErrorMessages = [
    // User errors
    'The email address or password you entered is incorrect.',
    'Quote request data invalid.',
    'There was a problem finding your quote. Please try refreshing and starting over.',
    'You already have an active policy',
    // Network errors
    'Loading CSS chunk',
    'Loading chunk',
    // Billing errors
    'Your card has insufficient funds',
    'Your card was declined',
    'An error occurred while processing your card',
    // Unsupported browsers
    'Promise.allSettled is not a function',
    // Other
    'Non-Error exception captured with keys: errorCauses, errorCode, errorId',
    'We cannot cover you at this time',
  ];

  if (
    expectedErrorMessages.some(
      (msg) =>
        event.message?.includes(msg) ||
        event.exception?.values?.some(({ value }) => value?.includes(msg))
    )
  ) {
    return null;
  }

  try {
    const json = JSON.stringify(event);

    const isVisualWebOptimizerError =
      json.includes('undefined is not an object') &&
      json.includes('dev.visualwebsiteoptimizer.com');

    const isExpectedAdpfError = json.includes(
      'throw Object.assign(new Error(), { code: knockoutErrorCode });'
    );

    if (isVisualWebOptimizerError || isExpectedAdpfError) {
      return null;
    }
  } catch {
    return event;
  }

  return event;
}

export function initIfEnabled() {
  if (isEnabled()) {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      release: process.env.PUBLIC_URL,
      integrations: [new BrowserTracing()],
      tracesSampleRate: 0.01,
      beforeSend: blockCommonErrorsFromBeingSent,
    });
  }
}

export function logException(
  ex: any,
  context: any
): Promise<string | undefined> {
  window.console?.error?.(ex);

  return new Promise((resolve, reject) => {
    const sentryConfig: CaptureContext & { errors?: any } = {
      extra: context,
      tags: {
        environment: process.env.REACT_APP_ENV,
        ...(!!('_hrq' in ex) && { hrq: ex._hrq }),
      },
    };

    if (ex instanceof Error) {
      resolve(Sentry.captureException(ex, sentryConfig));
    } else if (isPlainObject(ex) && (ex.message || ex.code)) {
      resolve(
        Sentry.captureException(new Error(ex.message || ex.code), sentryConfig)
      );
    } else if (isPlainObject(ex) && ex.errors && Array.isArray(ex.errors)) {
      sentryConfig.errors = ex.errors;
      resolve(Sentry.captureException(ex));
    } else if (isString(ex)) {
      resolve(Sentry.captureMessage(ex, sentryConfig));
    } else {
      reject();
    }
  });
}
